import { Link, useSearch } from '@tanstack/react-router';
import { type PaginationState, type SortingState } from '@tanstack/react-table';
import { format, subMonths } from 'date-fns';
import { CircleArrowLeft, Download, Search } from 'lucide-react';
import { type SetStateAction, useCallback, useEffect, useState } from 'react';
import type { DateRange } from 'react-day-picker';

import {
  downloadFilteredInvoicesCSVAPI,
  type InvoiceDateFilterField,
  useGetFilteredInvoices,
} from '~entities/payment/api/get-filtered-invoices';
import { PaymentStatus } from '~entities/payment/constants';

import { useGetCurrency } from '~shared/api/currency-api';
import { DatePickerWithRange } from '~shared/components/date-picker-with-range';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import useDebounce from '~shared/hooks/useDebounce';
import MultipleSelector, {
  type Option,
} from '~shared/shadcn/expansions/multiple-selector';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

import { InvoicesTable } from './invoices-table';

const DEFAULT_PAGE_SIZE = 20;

/** TanStack column id → API `sortBy` (see `FilteredInvoicesQueryDto`). */
const INVOICE_SORT_COLUMN_TO_API = {
  parent: 'parentName',
  student: 'studentName',
  dueDate: 'dueDate',
  paymentDate: 'paymentDate',
  className: 'className',
  sessionName: 'sessionName',
} as const;

type InvoiceListSortBy =
  (typeof INVOICE_SORT_COLUMN_TO_API)[keyof typeof INVOICE_SORT_COLUMN_TO_API];

const STATUS_OPTIONS: Array<Option> = Object.entries(PaymentStatus)
  .filter(([k]) => k !== 'ALL')
  .map(([, value]) => ({ value: value, label: value }));

export function Invoices() {
  const search = useSearch({ from: '/_auth/_layout/payment/invoices' });
  const { data: currencyDetails } = useGetCurrency();
  const currencySymbol = currencyDetails?.symbol ?? '$';

  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const end = new Date();
    const from = subMonths(end, 3);
    return { from, to: end };
  });
  const [dateField, setDateField] = useState<InvoiceDateFilterField>('dueDate');

  const parseStatusesFromSearch = (
    statusesParam: string | undefined,
  ): Array<Option> => {
    if (!statusesParam || typeof statusesParam !== 'string') return [];
    const values = statusesParam.split(',').map((s) => s.trim().toLowerCase());
    if (values.length === 0) return [];
    return STATUS_OPTIONS.filter((opt) =>
      values.includes(opt.value.toLowerCase()),
    );
  };
  const [statusOptions, setStatusOptions] = useState<Array<Option>>(() =>
    parseStatusesFromSearch(search.statuses),
  );

  useEffect(() => {
    const next = parseStatusesFromSearch(search.statuses);
    if (next.length > 0) setStatusOptions(next);
  }, [search.statuses]);
  const [finalAmountInput, setFinalAmountInput] = useState<string>('0');
  const [studentSearchInput, setStudentSearchInput] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(DEFAULT_PAGE_SIZE),
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [exportingCsv, setExportingCsv] = useState(false);

  const primarySort = sorting[0];
  const sortByApi: InvoiceListSortBy | undefined =
    primarySort != null && primarySort.id in INVOICE_SORT_COLUMN_TO_API
      ? INVOICE_SORT_COLUMN_TO_API[
          primarySort.id as keyof typeof INVOICE_SORT_COLUMN_TO_API
        ]
      : undefined;
  const sortOrderApi: 'asc' | 'desc' | undefined = (() => {
    if (primarySort == null || sortByApi == null) return undefined;
    return primarySort.desc ? 'desc' : 'asc';
  })();

  const handleSortingChange = useCallback(
    (updater: SetStateAction<SortingState>) => {
      setSorting((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        setPagination((p) => ({ ...p, pageIndex: 0 }));
        return next;
      });
    },
    [],
  );

  const startDate =
    dateRange?.from != null ? format(dateRange.from, 'yyyy-MM-dd') : undefined;
  const endDate =
    dateRange?.to != null ? format(dateRange.to, 'yyyy-MM-dd') : undefined;
  const statuses =
    statusOptions.length > 0 ? statusOptions.map((o) => o.value) : undefined;
  const finalAmount =
    finalAmountInput !== '' && !Number.isNaN(Number(finalAmountInput))
      ? Number(finalAmountInput)
      : undefined;
  const studentSearch = useDebounce(studentSearchInput.trim(), 500);

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const blob = await downloadFilteredInvoicesCSVAPI({
        startDate,
        endDate,
        dateField,
        statuses,
        finalAmount,
        studentSearch,
        sortBy: sortByApi,
        sortOrder: sortOrderApi,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `filtered-invoices-${startDate ?? 'all'}-to-${endDate ?? 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export CSV failed:', err);
    } finally {
      setExportingCsv(false);
    }
  };

  const { data, isLoading, isError, isFetching } = useGetFilteredInvoices({
    startDate,
    endDate,
    dateField,
    statuses,
    finalAmount,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    sortBy: sortByApi,
    sortOrder: sortOrderApi,
    studentSearch,
  });

  const apiPagination = data?.pagination;
  const totalCount = apiPagination?.totalCount ?? 0;
  const hasData = data != null;

  const resetToFirstPage = () => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  return (
    <div className='space-y-6'>
      <Link to='/payment/payment-dashboard' className='flex items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <h2 className='text-2xl font-semibold'>Invoices</h2>
      </Link>

      {/* Section 1: Total amount + Paid through wallet */}
      <div className='grid gap-4 md:grid-cols-2'>
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle>Total Amount</CardTitle>
            <CardDescription>
              Sum of final amounts for all invoices matching the current filters
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading && !hasData ? (
              <div className='text-sm text-muted-foreground'>Loading…</div>
            ) : (
              <div className='text-3xl font-bold'>
                {currencySymbol}
                {(data?.totalAmount ?? 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle>Paid through wallet</CardTitle>
            <CardDescription>
              Sum of amounts paid via wallet for invoices matching the current
              filters
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading && !hasData ? (
              <div className='text-sm text-muted-foreground'>Loading…</div>
            ) : (
              <div className='text-3xl font-bold'>
                {currencySymbol}
                {(data?.totalWalletAmount ?? 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Section 2: Filters + table */}
      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
          <CardDescription>
            Filter by date range, student name, statuses, and minimum amount
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex flex-wrap items-end gap-4'>
            <div className='space-y-2'>
              <Label>Date range</Label>
              <DatePickerWithRange
                date={dateRange}
                setDate={(range) => {
                  setDateRange(range);
                  resetToFirstPage();
                }}
              />
            </div>
            <div className='w-full min-w-0 space-y-2 sm:w-auto sm:min-w-[180px]'>
              <Label>Date applies to</Label>
              <Select
                value={dateField}
                onValueChange={(value: InvoiceDateFilterField) => {
                  setDateField(value);
                  resetToFirstPage();
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='dueDate'>Due date</SelectItem>
                  <SelectItem value='paymentDate'>Payment date</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='w-full min-w-0 space-y-2 sm:w-auto sm:min-w-[200px]'>
              <Label>Statuses</Label>
              <MultipleSelector
                defaultOptions={STATUS_OPTIONS}
                value={statusOptions}
                onChange={(opts) => {
                  setStatusOptions(opts);
                  resetToFirstPage();
                }}
                placeholder='Select statuses…'
                emptyIndicator={
                  <p className='text-center text-sm text-muted-foreground'>
                    No statuses found.
                  </p>
                }
              />
            </div>
            <div className='w-full space-y-2 sm:w-[140px]'>
              <Label>Minimum amount</Label>
              <Input
                type='number'
                min={0}
                step={0.01}
                placeholder='e.g. 100'
                value={finalAmountInput}
                onChange={(e) => {
                  setFinalAmountInput(e.target.value);
                  resetToFirstPage();
                }}
              />
            </div>
            <Button
              variant='secondary'
              onClick={() => {
                const end = new Date();
                setDateRange({ from: subMonths(end, 3), to: end });
                setDateField('dueDate');
                setStatusOptions([]);
                setFinalAmountInput('');
                setStudentSearchInput('');
                setSorting([]);
                resetToFirstPage();
              }}
            >
              Clear filters
            </Button>
            <Button
              variant='outline'
              onClick={handleExportCsv}
              disabled={exportingCsv}
            >
              <Download />
              {exportingCsv ? 'Exporting…' : 'Export to CSV'}
            </Button>
          </div>
          <div className='max-w-72 space-y-2'>
            <Label htmlFor='invoice-student-search'>Student</Label>
            <div className='relative'>
              <Input
                id='invoice-student-search'
                type='search'
                placeholder='Search by name…'
                value={studentSearchInput}
                onChange={(e) => {
                  setStudentSearchInput(e.target.value);
                  resetToFirstPage();
                }}
                autoComplete='off'
                className='relative pl-8'
              />
              <Search className='absolute left-2 top-1/2 size-4 -translate-y-1/2' />
            </div>
          </div>

          <InvoicesTable
            data={data?.invoices ?? []}
            currencySymbol={currencySymbol}
            isFetching={isFetching}
            pagination={pagination}
            onPaginationChange={setPagination}
            rowCount={totalCount}
            sorting={sorting}
            onSortingChange={handleSortingChange}
          />
        </CardContent>
      </Card>

      {isError && !hasData && (
        <div className='text-destructive'>Error loading invoices.</div>
      )}
    </div>
  );
}
